import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import PayrollDeclarationDialog from './PayrollDeclarationDialog';
import { TrendingUp, RefreshCw, Loader2, AlertTriangle, Info, FileDown } from 'lucide-react';
import { employeeAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août',
              'septembre', 'octobre', 'novembre', 'décembre'];

const euros = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('fr-FR')} €`);
const eurosSigne = (v) => (v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(Math.round(v)).toLocaleString('fr-FR')} €`);
const heures = (v) => (v ? `${Math.round(v).toLocaleString('fr-FR')} h` : '—');

// Ce que chaque salarié produit, ce qu'il coûte vraiment, et le résultat net qu'il
// dégage chaque mois. Le calcul est côté serveur (stats_salaries.py) ; cet écran
// ne fait que l'afficher, avec ce qu'il faut pour le lire sans se tromper.
const EmployeeStats = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [relecture, setRelecture] = useState(false);
  const [showDeclaration, setShowDeclaration] = useState(false);

  const charger = useCallback(async () => {
    setErreur('');
    try {
      setData(await employeeAPI.stats());
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Calcul impossible pour le moment');
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // La paie vit dans le classeur de tableau-de-bord, sur le PC. Sur le serveur, le bouton dépose
  // une demande ; l'agent du PC (paie_agent.py, tâche planifiée) la prend en une minute au plus,
  // lit la paie et la renvoie. On suit ici l'avancement, jusqu'à 2 minutes ; au-delà, la demande
  // reste en attente et sera traitée dès que le PC est allumé et connecté.
  const attendreAgent = async () => {
    const fin = Date.now() + 120000;
    while (Date.now() < fin) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const etat = await employeeAPI.payrollStatus();
      if (!etat.pending) return etat;
    }
    return null;
  };

  const relirePaie = async () => {
    setRelecture(true);
    try {
      const r = await employeeAPI.refreshPayroll();
      if (r.requested) {
        toast({ title: 'Relecture demandée', description: 'Le PC lit la paie, une minute au plus…', duration: 5000 });
        const etat = await attendreAgent();
        if (!etat) {
          toast({ title: 'Le PC n\'a pas encore répondu',
                  description: 'La demande reste en attente : elle sera traitée dès que le PC sera allumé et connecté à internet.',
                  variant: 'destructive', duration: 9000 });
          return;
        }
        toast({ title: 'Paie relue', description: `Paies définitives jusqu'à ${MOIS[(etat.paidUntil || 1) - 1]}` });
      } else {
        toast({ title: 'Paie relue', description: `Paies définitives jusqu'à ${MOIS[(r.paidUntil || 1) - 1]}` });
      }
      await charger();
    } catch (e) {
      toast({ title: 'Relecture impossible', description: e.response?.data?.detail || 'tableau-de-bord injoignable',
              variant: 'destructive' });
    } finally {
      setRelecture(false);
    }
  };

  const entete = (
    <>
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center text-blue-800">
            <TrendingUp className="w-5 h-5 mr-2" /> Contribution par salarié
          </CardTitle>
          {data?.available && (
            <p className="text-sm text-gray-500 mt-1">
              De {MOIS[data.fromMonth - 1]} à {MOIS[data.toMonth - 1]} {data.exercice} (mois de départ
              réglable dans Paramètres). Salaires : paies définitives de tableau-de-bord
              {data.payroll?.importedAt && `, relues le ${new Date(data.payroll.importedAt).toLocaleDateString('fr-FR')}`}.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDeclaration(true)}
                  title="Fichier pour le comptable : absences et motif, formations, jours par zone de trajet, paniers">
            <FileDown className="w-4 h-4 mr-2" />
            Déclarer les heures
          </Button>
          <Button variant="outline" size="sm" onClick={relirePaie} disabled={relecture}
                  title="À faire après une nouvelle paie saisie dans tableau-de-bord">
            {relecture ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Relire la paie
          </Button>
        </div>
      </div>
    </CardHeader>
    <PayrollDeclarationDialog isOpen={showDeclaration} onOpenChange={setShowDeclaration} />
    </>
  );

  if (erreur || (data && !data.available)) {
    return (
      <Card className="border-blue-200">
        {entete}
        <CardContent>
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {erreur || data.reason}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="border-blue-200">
        {entete}
        <CardContent><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200">
      {entete}
      <CardContent className="space-y-3">
        {data.unmappedPayrollLines?.length > 0 && (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
            <div className="font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Lignes de paie rattachées à personne
            </div>
            {data.unmappedPayrollLines.map((l) => (
              <div key={l.line}>« {l.line} » — {euros(l.total)} sur l'exercice, non compté dans le tableau</div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3 font-medium">Salarié</th>
                <th className="py-2 px-2 font-medium text-right">Mois</th>
                <th className="py-2 px-2 font-medium text-right">Heures</th>
                <th className="py-2 px-2 font-medium text-right">Valeur produite</th>
                <th className="py-2 px-2 font-medium text-right">Coût réel</th>
                <th className="py-2 px-2 font-medium text-right" title="Coût réel divisé par les heures pointées">Coût / h</th>
                <th className="py-2 px-2 font-medium text-right"
                    title="Valeur produite moins son coût réel, sur la période. Les frais de structure ne sont pas déduits : ils sont en bas du tableau">
                  Contribution
                </th>
                <th className="py-2 pl-2 font-medium text-right">Contribution / mois</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.employeeId} className={`border-b last:border-0 ${r.archived ? 'text-gray-500' : ''}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-800">{r.name}</div>
                    <div className="flex gap-1 mt-0.5">
                      {r.isManager && (
                        <Badge variant="outline" className="text-xs text-blue-700 border-blue-300"
                               title="Sa rémunération paie aussi la gestion, les devis et le commercial : le résultat ne mesure pas son seul travail sur les chantiers">
                          dirigeant
                        </Badge>
                      )}
                      {r.archived && <Badge variant="outline" className="text-xs">ancien</Badge>}
                      {!r.hasCostSource && !r.noActivity && (
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300"
                               title="Ni ligne de paie reliée, ni coût horaire sur sa fiche : coût réel inconnu">
                          coût inconnu
                        </Badge>
                      )}
                    </div>
                    {r.noActivity && (
                      <div className="text-xs text-amber-700 mt-0.5">
                        Aucune heure pointée depuis {MOIS[data.fromMonth - 1]}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">{r.noActivity ? '—' : r.months}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{heures(r.hours)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    {r.noActivity ? '—' : euros(r.value)}
                    {r.valueEstimated > 0.5 && (
                      <div className="text-xs text-gray-400" title="Part venant de chantiers en cours, estimée sur le devis">
                        dont {euros(r.valueEstimated)} estimés
                      </div>
                    )}
                  </td>
                  {/* Sans source de coût, un « résultat » serait toute la valeur produite :
                      faux et flatteur. On n'affiche rien plutôt qu'un chiffre trompeur. */}
                  <td className="py-2 px-2 text-right tabular-nums">
                    {r.hasCostSource && !r.noActivity ? euros(r.realCost) : '—'}
                    {r.costSource === 'hourly' && !r.noActivity && (
                      <div className="text-xs text-gray-400" title="Indépendant : heures pointées × coût horaire de sa fiche">
                        au taux horaire
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    {r.hasCostSource && r.realCostPerHour != null
                      ? `${r.realCostPerHour.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} €` : '—'}
                  </td>
                  <td className={`py-2 px-2 text-right tabular-nums ${r.net < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                    {r.hasCostSource && !r.noActivity ? eurosSigne(r.net) : '—'}
                  </td>
                  <td className={`py-2 pl-2 text-right tabular-nums font-semibold ${
                    !r.hasCostSource || r.noActivity ? 'text-gray-400' : r.netPerMonth < 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {r.hasCostSource ? eurosSigne(r.netPerMonth) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            {data.totals?.structurePerMonth != null && (
              <tfoot className="border-t-2 border-gray-200">
                <tr>
                  <td colSpan={7} className="pt-3 pb-1 pr-2 text-right text-gray-600">
                    Contribution de l'équipe actuelle
                  </td>
                  <td className="pt-3 pb-1 pl-2 text-right tabular-nums font-medium">
                    {eurosSigne(data.totals.teamContributionPerMonth)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={7} className="py-1 pr-2 text-right text-gray-600"
                      title={`Charges fixes ${euros(data.totals.structureDetail?.chargesFixesPerMonth)} + amortissements ${euros(data.totals.structureDetail?.amortissementsPerMonth)} par mois`}>
                    Frais de structure (budget {data.exercice} de tableau-de-bord)
                  </td>
                  <td className="py-1 pl-2 text-right tabular-nums text-red-700">
                    {eurosSigne(-data.totals.structurePerMonth)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={7} className="pt-1 pb-2 pr-2 text-right font-semibold text-gray-800">
                    Reste par mois
                  </td>
                  <td className={`pt-1 pb-2 pl-2 text-right tabular-nums font-bold ${
                    data.totals.remainingPerMonth < 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {eurosSigne(data.totals.remainingPerMonth)}
                  </td>
                </tr>
                {data.totals.excludedWithoutCost?.length > 0 && (
                  <tr>
                    <td colSpan={8} className="pb-2 text-right text-xs text-amber-700">
                      Non compté(s), faute de coût connu : {data.totals.excludedWithoutCost.join(', ')}
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>

        <p className="text-xs text-gray-500 flex gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Valeur produite : chaque heure pointée vaut (facturé, ou devis à défaut, moins achats)
            divisé par les heures du chantier — la formule de l'onglet SALARIÉS. Coût réel : salaire
            chargé de la paie, aides réparties sur l'année ; pour un indépendant, ses heures au coût horaire de sa fiche. Contribution : ce que
            le salarié apporte pour payer la structure — rien de celle-ci n'est réparti par tête,
            elle est déduite une seule fois, en bas. Ordre de grandeur, pas le résultat comptable :
            celui-ci est dans tableau-de-bord.
          </span>
        </p>
      </CardContent>
    </Card>
  );
};

export default EmployeeStats;
